import * as React from 'react';
import { Link as ReactRouterLink } from 'react-router';

import { compose, getDisplayName, setDisplayName } from 'recompose';

import Retracked from 'js/app/retracked';
import useRouter, { useNavigate } from 'js/lib/useRouter';

import { Typography2 } from '@coursera/cds-core';

import { closeMiniModalWithRoute, useRouterParams } from 'bundles/enterprise-legacy-xdp/components/ProgramMiniModal';
import { getProductPath, launchMiniModal } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import { IdTypes } from 'bundles/product-features';
import ExperimentalProgramFullScreenModal from 'bundles/program-common/components/ExperimentalProgramFullScreenModal';
import type { Product, ProductType } from 'bundles/program-common/types/programCommon';
import EnterpriseBanner from 'bundles/program-home/components/enterprise-home/EnterpriseBanner';
import type { PropsFromEnterpriseHomeApp } from 'bundles/program-home/components/enterprise-home/EnterpriseHomeApp';
import EnterpriseHomeTabs from 'bundles/program-home/components/enterprise-home/EnterpriseHomeTabs';
import { EnterpriseHomeHorizontalDomainsMenu } from 'bundles/program-home/components/multiprogram/HorizontalDomainsMenu';
import { useProductFeatures } from 'bundles/program-home/productFeatureFlags';

import _t from 'i18n!nls/program-home';

import 'css!./__styles__/EnterpriseHomePage';

type Props = PropsFromEnterpriseHomeApp;

export function EnterpriseHomePage({
  thirdPartyOrganization,
  userId,
  enterprisePrograms,
  programInvitations,
  userEmails,
  isBrowseOnlyProgram,
  allowOrgForCurriculumBuilder,
  allowOrgForSpecializationConfiguration,
  shouldShowSponsoredByMessage,
  domains,
  subdomains,
  enableSkillsDashboard,
  showSkillSetRoleFilter,
}: Props) {
  const router = useRouter();
  const navigate = useNavigate();
  const onProductCardClick = React.useCallback(
    (product: Product, productType: ProductType, collectionId?: string | null) => {
      launchMiniModal(router, product.id, productType === 'course', collectionId || '', undefined, undefined, {
        slug: product.slug,
        productPagePath: getProductPath(product),
      });
    },
    [router]
  );
  const onCloseMiniModal = React.useCallback(() => closeMiniModalWithRoute(router), [router]);

  const { features, loading: isFeaturesLoading } = useProductFeatures({
    idType: IdTypes.Organization,
    id: thirdPartyOrganization?.id || '',
    skip: !thirdPartyOrganization?.id,
    ssr: true,
  });

  // TODO(ppaskaris): loading state
  if (!thirdPartyOrganization || !userId) {
    return null;
  }

  // if feature not loaded, don't show anything
  if (isFeaturesLoading) {
    return null;
  }

  // redirect to `/programs/landing/orgSlug` if `enableOrgHomepage` is true
  if (features.get('enterprise', 'enableOrgHomepage')) {
    navigate('/programs/landing/' + thirdPartyOrganization.slug);
    return null;
  }

  const isMultiProgram = enterprisePrograms.length > 1;

  // Not a react hook, just a poorly aged identifier.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const routerParams = useRouterParams(router);

  return (
    <div className="rc-EnterpriseHomePage">
      <EnterpriseHomeHorizontalDomainsMenu domains={domains} subdomains={subdomains} />
      <div role="main">
        <EnterpriseBanner
          thirdPartyOrganization={thirdPartyOrganization}
          isMultiProgram={isMultiProgram}
          isProgramDetailsView={false}
          shouldShowJoinButton={false}
          isUpswell={false}
          isC4cv={false}
        />
        <EnterpriseHomeTabs
          isMultiProgram={isMultiProgram}
          enterprisePrograms={enterprisePrograms}
          programInvitations={programInvitations}
          userId={userId}
          onProductCardClick={onProductCardClick}
          thirdPartyOrganization={thirdPartyOrganization}
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          thirdPartyOrgSlug={thirdPartyOrganization.slug}
          userEmails={userEmails}
          allowOrgForCurriculumBuilder={allowOrgForCurriculumBuilder}
          allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
          enableSkillsDashboard={enableSkillsDashboard}
          showSkillSetRoleFilter={showSkillSetRoleFilter}
        />
      </div>
      {routerParams && (
        <ExperimentalProgramFullScreenModal
          thirdPartyOrganizationName={thirdPartyOrganization.name}
          renderCourseHomeLink
          onCloseMiniModal={onCloseMiniModal}
          isUpswell={false}
          hideAction={isBrowseOnlyProgram}
          disableSponsoredByMessage={!shouldShowSponsoredByMessage}
          thirdPartyOrgId={thirdPartyOrganization.id}
          spookyMultiprogramFlag
          {...routerParams}
          thirdPartyOrganizationSlug={thirdPartyOrganization.slug}
          programs={enterprisePrograms}
          breadcrumbExtraMargin
          breadcrumb={
            <ReactRouterLink to={`/programs/all/${thirdPartyOrganization.slug}`}>
              <Typography2 variant="subtitleMedium" component="div" color="highlightBlue">
                {_t('All Learning Programs')}
              </Typography2>
            </ReactRouterLink>
          }
        />
      )}
    </div>
  );
}

export default compose<Props, Props>(
  setDisplayName(getDisplayName(EnterpriseHomePage)),
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'program_home_v3',
      page: 'enterprise_home_page',
    },
  }))
)(EnterpriseHomePage);
